import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star, Building2, Calendar, MessageSquare, TrendingUp, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeedbackRiepilogoCandidato = ({ candidateId }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFeedbacks: 0,
    averageRating: 0
  });

  useEffect(() => {
    fetchFeedbacks();
  }, [candidateId]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/v1/matches?candidate_id=${candidateId}`);
      
      // Filter only matches where candidate submitted feedback
      const candidateFeedbacks = response.data.filter(
        match => match.feedback_candidato_data
      );
      
      setFeedbacks(candidateFeedbacks);
      
      // Calculate stats
      const total = candidateFeedbacks.length;
      const avgRating = total > 0 
        ? candidateFeedbacks.reduce((sum, f) => sum + (f.valutazione_candidato || 0), 0) / total 
        : 0;
      
      setStats({
        totalFeedbacks: total,
        averageRating: avgRating.toFixed(1)
      });
      
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error('Errore nel caricamento feedback');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-8 h-8 text-purple-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">I Miei Feedback</h2>
          <p className="text-sm text-gray-600">Feedback che hai lasciato alle agenzie</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Totale Feedback</p>
                <p className="text-2xl font-bold text-purple-900">{stats.totalFeedbacks}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Media Rating</p>
                <p className="text-2xl font-bold text-yellow-900">{stats.averageRating}</p>
              </div>
              <Star className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          Storico Feedback Inviati
        </h3>

        {feedbacks.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Non hai ancora inviato nessun feedback</p>
              <p className="text-sm text-gray-500 mt-2">
                Dopo un colloquio, puoi lasciare un feedback all'agenzia dalla sezione "Opportunità"
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {feedbacks.map((feedback) => (
              <Card key={feedback.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Building2 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{feedback.agency_name}</h4>
                          <p className="text-sm text-gray-600">{feedback.agency_city}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Inviato
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Valutazione:</span>
                      {renderStars(feedback.valutazione_candidato || 0)}
                      <span className="text-sm font-medium text-gray-700">
                        {feedback.valutazione_candidato || 0}/5
                      </span>
                    </div>

                    {/* Feedback Text */}
                    {feedback.feedback_candidato && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-800">{feedback.feedback_candidato}</p>
                      </div>
                    )}

                    {/* Date */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t">
                      <Calendar className="w-3 h-3" />
                      <span>Inviato il {formatDate(feedback.feedback_candidato_data)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackRiepilogoCandidato;
